import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowLeft, Building2, Pencil, Plus, Trash2 } from 'lucide-react';
import type { CustomerBranchToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { FormField } from '~/components/system/form-field';
import { serverMessage } from '~/lib/api/server-error';
import { useCustomerBranches, useCustomerBranchMutations } from '~/hooks/use-customer-subviews';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	customerId: number | null;
	customerName?: string;
}

const EMPTY: CustomerBranchToken = {
	cid: 0,
	nm: '',
	cd: '',
	eml: '',
	tel: '',
	adl: '',
	ar: '',
	ct: '',
	pc: '',
	erpcd: '',
	iv: true,
	si: 0
};

/** Per-customer branches sub-grid (legacy CustomerBranches popup). List + inline add/edit. */
export function CustomerBranchesDialog({ open, onOpenChange, customerId, customerName }: Props) {
	const { t } = useTranslation();
	const { data, isLoading } = useCustomerBranches(open ? customerId : null);
	const { create, update, remove } = useCustomerBranchMutations(customerId ?? 0);
	const [editing, setEditing] = useState<CustomerBranchToken | null>(null);

	useEffect(() => {
		if (!open) setEditing(null);
	}, [open]);

	const save = async () => {
		if (!editing) return;
		if (!editing.nm?.trim()) {
			toast.error(t('validation.required'));
			return;
		}
		try {
			if (editing.id) await update.mutateAsync(editing);
			else await create.mutateAsync(editing);
			toast.success(t('common.save'));
			setEditing(null);
		} catch (err) {
			toast.error(serverMessage(err));
		}
	};

	const del = (b: CustomerBranchToken) => {
		if (!b.id) return;
		remove.mutate(b.id, { onSuccess: () => toast.success(t('common.delete')), onError: e => toast.error(serverMessage(e)) });
	};

	const set = (patch: Partial<CustomerBranchToken>) => setEditing(prev => ({ ...(prev ?? EMPTY), ...patch }));

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-[95vw] sm:max-w-3xl">
				<DialogHeader>
					<DialogTitle>{t('customers.list.branches.title')}</DialogTitle>
					<DialogDescription>{customerName || t('customers.list.branches.subtitle')}</DialogDescription>
				</DialogHeader>

				{editing ? (
					<div className="flex flex-col gap-3">
						<div className="grid grid-cols-2 gap-3">
							<FormField label={t('customers.list.branches.code')}>
								<Input value={editing.cd ?? ''} onChange={e => set({ cd: e.target.value })} maxLength={50} />
							</FormField>
							<FormField label={t('customers.list.branches.name')}>
								<Input value={editing.nm ?? ''} onChange={e => set({ nm: e.target.value })} maxLength={100} autoFocus />
							</FormField>
							<FormField label={t('customers.list.branches.email')}>
								<Input value={editing.eml ?? ''} onChange={e => set({ eml: e.target.value })} maxLength={255} />
							</FormField>
							<FormField label={t('customers.list.branches.tel')}>
								<Input value={editing.tel ?? ''} onChange={e => set({ tel: e.target.value })} maxLength={20} />
							</FormField>
							<FormField label={t('customers.list.branches.address')}>
								<Input value={editing.adl ?? ''} onChange={e => set({ adl: e.target.value })} maxLength={200} />
							</FormField>
							<FormField label={t('customers.list.branches.area')}>
								<Input value={editing.ar ?? ''} onChange={e => set({ ar: e.target.value })} maxLength={50} />
							</FormField>
							<FormField label={t('customers.list.branches.city')}>
								<Input value={editing.ct ?? ''} onChange={e => set({ ct: e.target.value })} maxLength={50} />
							</FormField>
							<FormField label={t('customers.list.branches.postalCode')}>
								<Input value={editing.pc ?? ''} onChange={e => set({ pc: e.target.value })} maxLength={20} />
							</FormField>
							<FormField label={t('customers.list.branches.erpCode')}>
								<Input value={editing.erpcd ?? ''} onChange={e => set({ erpcd: e.target.value })} maxLength={50} />
							</FormField>
							<FormField label={t('customers.list.branches.sort')}>
								<Input
									type="number"
									value={editing.si ?? 0}
									onChange={e => set({ si: e.target.value === '' ? 0 : Number(e.target.value) })}
								/>
							</FormField>
						</div>
						<div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
							<Label htmlFor="br-iv">{t('customers.list.branches.visible')}</Label>
							<Switch id="br-iv" checked={Boolean(editing.iv)} onCheckedChange={next => set({ iv: next })} />
						</div>
						<div className="flex justify-between">
							<Button variant="ghost" onClick={() => setEditing(null)}>
								<ArrowLeft size={15} /> {t('common.cancel')}
							</Button>
							<Button onClick={save} disabled={create.isPending || update.isPending}>
								{t('common.save')}
							</Button>
						</div>
					</div>
				) : (
					<div className="flex flex-col gap-3">
						<div className="flex justify-end">
							<Button size="sm" onClick={() => setEditing({ ...EMPTY, cid: customerId ?? 0 })}>
								<Plus size={15} /> {t('customers.list.branches.add')}
							</Button>
						</div>
						<div className="max-h-[55vh] overflow-auto rounded-md border border-border">
							{isLoading ? (
								<div className="flex flex-col gap-2 p-3">
									{Array.from({ length: 4 }).map((_, i) => (
										<Skeleton key={i} className="h-9 w-full rounded-md" />
									))}
								</div>
							) : !data || data.length === 0 ? (
								<div className="flex flex-col items-center gap-3 py-12 text-center">
									<Building2 size={32} className="text-foreground-disabled" strokeWidth={1.25} />
									<p className="text-sm text-foreground-muted">{t('common.noResults')}</p>
								</div>
							) : (
								<table className="w-full border-collapse text-sm">
									<thead className="bg-elevated text-xs text-foreground-muted">
										<tr className="border-b border-border">
											<th className="px-3 py-2 text-left font-medium">{t('customers.list.branches.code')}</th>
											<th className="px-3 py-2 text-left font-medium">{t('customers.list.branches.name')}</th>
											<th className="px-3 py-2 text-left font-medium">{t('customers.list.branches.city')}</th>
											<th className="px-2 py-2" style={{ width: 80 }} />
										</tr>
									</thead>
									<tbody>
										{data.map(b => (
											<tr key={b.id} className="border-b border-border/60 hover:bg-muted/50">
												<td className="px-3 py-2 font-mono text-xs text-foreground-muted">{b.cd || '—'}</td>
												<td className="px-3 py-2 text-foreground">{b.nm}</td>
												<td className="px-3 py-2 text-foreground-muted">{b.ct || '—'}</td>
												<td className="px-2 py-1 text-center whitespace-nowrap">
													<Button
														variant="ghost"
														size="icon"
														className="h-7 w-7"
														aria-label={t('common.edit')}
														onClick={() => setEditing(b)}>
														<Pencil size={14} />
													</Button>
													<Button
														variant="ghost"
														size="icon"
														className="h-7 w-7 text-danger-fg"
														aria-label={t('common.delete')}
														onClick={() => del(b)}>
														<Trash2 size={14} />
													</Button>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							)}
						</div>
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}
